/**
 * UI Translations for DP Portal
 * Simple translation system for consent record page and other UI elements
 */

export interface TranslationMap {
    // Consent Record Page
    consentRecordDetails: string;
    consentInformation: string;
    technicalDetails: string;
    consentDate: string;
    language: string;
    source: string;
    ipAddress: string;
    location: string;
    collectionPoint: string;

    // Tabs
    purposes: string;
    consentPurposes: string;
    dataCollected: string;
    privacyPolicy: string;
    yourRights: string;
    contactDpo: string;

    // Data Elements
    dataElementsCollected: string;
    dataElementsDescription: string;
    fieldNamesShownIn: string;
    type: string;
    value: string;
    personalData: string;
    nonPersonalData: string;
    showPersonalData: string;
    hidePersonalData: string;
    personalDataTooltip: string;
    personalDataRevealed: string;

    // Actions
    print: string;
    exportPdf: string;
    goBack: string;
    viewFullConsentRecord: string;

    // Purpose Details
    purposesForConsent: string;
    purposeKey: string;
    retentionExpires: string;

    // Common
    loading: string;
    error: string;
    notFound: string;
    noDataAvailable: string;

    // Notices
    notices: string;
    notice: string;
    noticeDetails: string;
    acknowledge: string;
    acknowledged: string;
    unacknowledged: string;
    acknowledgeNotice: string;
    noticeAcknowledged: string;
    acknowledgeConfirmation: string;
    noticeType: string;
    assignedDate: string;
    effectiveDate: string;
    sentOn: string;
    appliesFrom: string;
    noticeNotYetEffective: string;
    acknowledgedDate: string;
    consented: string;
    consentRecorded: string;
    consentRecordedDescription: string;
    viewDetails: string;
    close: string;
    allNotices: string;
    consentNotices: string;
    breachNotices: string;
    erasureNotices: string;
    informationalNotices: string;
    showAcknowledged: string;
    hideAcknowledged: string;
    totalNotices: string;
    actionRequired: string;
    noNotices: string;
    noNoticesMessage: string;
    lawfulGround: string;
    information: string;
    affectedData: string;
    consequences: string;
    measuresTaken: string;
    recommendedPrecautions: string;
    contactInfo: string;
    dataToBeErased: string;
    reason: string;
    retentionPeriod: string;
    retention: string;
    dpdpaNotice: string;
    compliance: string;
    retentionUnitYear: string;
    retentionUnitYears: string;
    retentionUnitMonth: string;
    retentionUnitMonths: string;
    retentionUnitDay: string;
    retentionUnitDays: string;
    retentionUntilConsentWithdrawn: string;
    retentionUntilPurposeFulfilled: string;
    retentionUntilRetentionExpires: string;
    retentionUntilErasureRequested: string;
    retentionUntilAccountClosure: string;
    guardianMinorNoticeBlurbWithName: string;
    guardianMinorNoticeBlurbGeneric: string;
}

export const translations: Record<string, TranslationMap> = {
    en: {
        // Consent Record Page
        consentRecordDetails: "Consent Record Details",
        consentInformation: "Consent Information",
        technicalDetails: "Technical Details",
        consentDate: "Consent Date",
        language: "Language",
        source: "Source",
        ipAddress: "IP Address",
        location: "Location",
        collectionPoint: "Collection Point",

        // Tabs
        purposes: "Purposes",
        consentPurposes: "Consent Purposes",
        dataCollected: "Data Collected",
        privacyPolicy: "Privacy Policy",
        yourRights: "Your Rights",
        contactDpo: "Contact DPO",

        // Data Elements
        dataElementsCollected: "Data Elements Collected",
        dataElementsDescription: "The personal data that was collected as part of this consent",
        fieldNamesShownIn: "Field names shown in",
        type: "Type",
        value: "Value",
        personalData: "Personal Data",
        nonPersonalData: "Non-Personal Data",
        showPersonalData: "Show Personal Data",
        hidePersonalData: "Hide Personal Data",
        personalDataTooltip: "Click to reveal/hide personal data. Actions are logged.",
        personalDataRevealed: "Personal data is now visible. This action has been logged for security purposes.",

        // Actions
        print: "Print",
        exportPdf: "Export PDF",
        goBack: "Go Back",
        viewFullConsentRecord: "View Full Consent Record",

        // Purpose Details
        purposesForConsent: "The purposes for which you provided consent on",
        purposeKey: "Purpose Key",
        retentionExpires: "Retention Expires",

        // Common
        loading: "Loading",
        error: "Error",
        notFound: "Not Found",
        noDataAvailable: "No data available",

        // Notices
        notices: "Notices",
        notice: "Notice",
        noticeDetails: "Notice Details",
        acknowledge: "Acknowledge",
        acknowledged: "Acknowledged",
        unacknowledged: "Unacknowledged",
        acknowledgeNotice: "Acknowledge Notice",
        noticeAcknowledged: "Notice Acknowledged",
        acknowledgeConfirmation: "Are you sure you want to acknowledge this notice?",
        noticeType: "Notice Type",
        assignedDate: "Assigned Date",
        effectiveDate: "Effective Date",
        sentOn: "Sent on",
        appliesFrom: "Applies from",
        noticeNotYetEffective: "This notice applies from {date}. You can respond on or after that date.",
        acknowledgedDate: "Acknowledged Date",
        consented: "Consented",
        consentRecorded: "Consent recorded",
        consentRecordedDescription: "You have already given consent for this notice. You can view your consent history under Forms or Purposes.",
        viewDetails: "View Details",
        close: "Close",
        allNotices: "All Notices",
        consentNotices: "Consent Notices",
        breachNotices: "Breach Notices",
        erasureNotices: "Erasure Notices",
        informationalNotices: "Informational Notices",
        showAcknowledged: "Show Acknowledged",
        hideAcknowledged: "Hide Acknowledged",
        totalNotices: "Total Notices",
        actionRequired: "Action Required",
        noNotices: "No Notices",
        noNoticesMessage: "You don't have any notices at this time.",
        lawfulGround: "Lawful Ground",
        information: "Information",
        affectedData: "Affected Data",
        consequences: "Consequences",
        measuresTaken: "Measures Taken",
        recommendedPrecautions: "Recommended Precautions",
        contactInfo: "Contact Information",
        dataToBeErased: "Data to be Erased",
        reason: "Reason",
        retentionPeriod: "Retention Period",
        retention: "Retention",
        dpdpaNotice: "This notice is provided in compliance with the Digital Personal Data Protection Act, 2023 (DPDPA).",
        compliance: "Compliance",
        retentionUnitYear: "year",
        retentionUnitYears: "years",
        retentionUnitMonth: "month",
        retentionUnitMonths: "months",
        retentionUnitDay: "day",
        retentionUnitDays: "days",
        retentionUntilConsentWithdrawn: "until consent withdrawn",
        retentionUntilPurposeFulfilled: "until purpose fulfilled",
        retentionUntilRetentionExpires: "until retention period expires",
        retentionUntilErasureRequested: "until erasure requested",
        retentionUntilAccountClosure: "until account closure",
        guardianMinorNoticeBlurbWithName: "You are viewing this notice on behalf of a minor: {name}.",
        guardianMinorNoticeBlurbGeneric: "You are viewing this notice on behalf of a minor Data Principal."
    },

    hi: {
        // Consent Record Page
        consentRecordDetails: "सहमति रिकॉर्ड विवरण",
        consentInformation: "सहमति जानकारी",
        technicalDetails: "तकनीकी विवरण",
        consentDate: "सहमति दिनांक",
        language: "भाषा",
        source: "स्रोत",
        ipAddress: "आईपी पता",
        location: "स्थान",
        collectionPoint: "संग्रह बिंदु",

        // Tabs
        purposes: "उद्देश्य",
        consentPurposes: "सहमति उद्देश्य",
        dataCollected: "एकत्रित डेटा",
        privacyPolicy: "गोपनीयता नीति",
        yourRights: "आपके अधिकार",
        contactDpo: "डीपीओ से संपर्क करें",

        // Data Elements
        dataElementsCollected: "एकत्रित डेटा तत्व",
        dataElementsDescription: "इस सहमति के भाग के रूप में एकत्रित व्यक्तिगत डेटा",
        fieldNamesShownIn: "फ़ील्ड नाम दिखाए गए",
        type: "प्रकार",
        value: "मूल्य",
        personalData: "व्यक्तिगत डेटा",
        nonPersonalData: "गैर-व्यक्तिगत डेटा",
        showPersonalData: "व्यक्तिगत डेटा दिखाएं",
        hidePersonalData: "व्यक्तिगत डेटा छुपाएं",
        personalDataTooltip: "व्यक्तिगत डेटा दिखाने/छुपाने के लिए क्लिक करें। क्रियाएं लॉग की जाती हैं।",
        personalDataRevealed: "व्यक्तिगत डेटा अब दिखाई दे रहा है। सुरक्षा उद्देश्यों के लिए यह क्रिया लॉग की गई है।",

        // Actions
        print: "प्रिंट करें",
        exportPdf: "पीडीएफ निर्यात करें",
        goBack: "वापस जाएं",
        viewFullConsentRecord: "पूरा सहमति रिकॉर्ड देखें",

        // Purpose Details
        purposesForConsent: "उन उद्देश्यों के लिए जिनके लिए आपने सहमति दी थी",
        purposeKey: "उद्देश्य कुंजी",
        retentionExpires: "प्रतिधारण समाप्त",

        // Common
        loading: "लोड हो रहा है",
        error: "त्रुटि",
        notFound: "नहीं मिला",
        noDataAvailable: "कोई डेटा उपलब्ध नहीं",

        // Notices
        notices: "नोटिस",
        notice: "नोटिस",
        noticeDetails: "नोटिस विवरण",
        acknowledge: "स्वीकार करें",
        acknowledged: "स्वीकृत",
        unacknowledged: "अस्वीकृत",
        acknowledgeNotice: "नोटिस स्वीकार करें",
        noticeAcknowledged: "नोटिस स्वीकार कर ली गई",
        acknowledgeConfirmation: "क्या आप वाकई इस नोटिस को स्वीकार करना चाहते हैं?",
        noticeType: "नोटिस प्रकार",
        assignedDate: "नियत तिथि",
        effectiveDate: "प्रभावी तिथि",
        sentOn: "भेजा गया",
        appliesFrom: "प्रभावी तिथि से",
        noticeNotYetEffective: "यह नोटिस {date} से लागू होती है। आप उस तिथि या उसके बाद प्रतिक्रिया दे सकते हैं।",
        acknowledgedDate: "स्वीकृत तिथि",
        consented: "सहमति दी",
        consentRecorded: "सहमति दर्ज",
        consentRecordedDescription: "आपने पहले ही इस नोटिस के लिए सहमति दे दी है। आप फॉर्म या उद्देश्य के तहत अपना सहमति इतिहास देख सकते हैं।",
        viewDetails: "विवरण देखें",
        close: "बंद करें",
        allNotices: "सभी नोटिस",
        consentNotices: "सहमति नोटिस",
        breachNotices: "उल्लंघन नोटिस",
        erasureNotices: "मिटाने की नोटिस",
        informationalNotices: "सूचनात्मक नोटिस",
        showAcknowledged: "स्वीकृत दिखाएं",
        hideAcknowledged: "स्वीकृत छुपाएं",
        totalNotices: "कुल नोटिस",
        actionRequired: "कार्रवाई आवश्यक",
        noNotices: "कोई नोटिस नहीं",
        noNoticesMessage: "आपके पास इस समय कोई नोटिस नहीं है।",
        lawfulGround: "वैध आधार",
        information: "जानकारी",
        affectedData: "प्रभावित डेटा",
        consequences: "परिणाम",
        measuresTaken: "उठाए गए उपाय",
        recommendedPrecautions: "अनुशंसित सावधानियां",
        contactInfo: "संपर्क जानकारी",
        dataToBeErased: "मिटाए जाने वाला डेटा",
        reason: "कारण",
        retentionPeriod: "प्रतिधारण अवधि",
        retention: "प्रतिधारण",
        dpdpaNotice: "यह नोटिस डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम, 2023 (DPDPA) के अनुपालन में प्रदान किया गया है।",
        compliance: "अनुपालन",
        retentionUnitYear: "वर्ष",
        retentionUnitYears: "वर्ष",
        retentionUnitMonth: "महीना",
        retentionUnitMonths: "महीने",
        retentionUnitDay: "दिन",
        retentionUnitDays: "दिन",
        retentionUntilConsentWithdrawn: "जब तक सहमति वापस नहीं ली जाती",
        retentionUntilPurposeFulfilled: "जब तक उद्देश्य पूरा नहीं हो जाता",
        retentionUntilRetentionExpires: "जब तक प्रतिधारण अवधि समाप्त नहीं हो जाती",
        retentionUntilErasureRequested: "जब तक मिटाने का अनुरोध नहीं किया जाता",
        retentionUntilAccountClosure: "जब तक खाता बंद नहीं हो जाता",
        guardianMinorNoticeBlurbWithName: "आप यह नोटिस एक नाबालिग की ओर से देख रहे हैं: {name}।",
        guardianMinorNoticeBlurbGeneric: "आप यह नोटिस एक नाबालिग डेटा प्रधान की ओर से देख रहे हैं।"
    }
};

/**
 * Get translation for a given key and language
 * Falls back to English if translation not found
 */
export function getTranslation(key: keyof TranslationMap, languageCode: string): string {
    const lang = languageCode.toLowerCase();
    const translation = translations[lang]?.[key];

    if (translation) {
        return translation;
    }

    // Fallback to English
    return translations.en[key] || key;
}

/**
 * Get all translations for a language
 * Falls back to English for missing keys
 */
export function getTranslations(languageCode: string): TranslationMap {
    const lang = languageCode.toLowerCase();
    const langTranslations = translations[lang] || {};
    const englishTranslations = translations.en;

    // Merge with English fallbacks
    return { ...englishTranslations, ...langTranslations };
}
